
import React, { useEffect, useState, useRef } from 'react';
import Header from '../Header';
import Footer from '../Footer';
import config from '../../config';
import { useApi } from '../../ApiContext';
import PopupService from '../PopupService';

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const DisplayClientDataInfo = () => {
  const username = sessionStorage.getItem("username");
  const mrToken = localStorage.getItem("mr_token");
  const { callApi, setIsApiLoading } = useApi();
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [loading, setLoading] = useState(false);

  // Table state
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('file_date');
  const [sortOrder, setSortOrder] = useState('desc');
  const searchInputRef = useRef();  
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [jumpPage, setJumpPage] = useState('');
  // Calculate window of 10 pages
  const windowSize = 10;
  const startPage = Math.floor((page - 1) / windowSize) * windowSize + 1;
  const endPage = Math.min(startPage + windowSize - 1, totalPages);
  const pageNumbers = [];
  for (let i = startPage; i <= endPage; i++) {
    pageNumbers.push(i);
  }

  useEffect(() => {
    // Load clients on mount
    const fetchClients = async () => {
      setLoading(true);
      await callApi(async () => {
        setIsApiLoading(true);
        try {
          const response = await fetch(`${config.API_BASE_URL}/activeclients`,
            { headers: { 'Content-Type': 'application/json', 'X-Username': username, 'Authorization': `Bearer ${mrToken}` } }
          );
          if (response.ok) {
            const data = await response.json();
            setClients(Array.isArray(data.data.ACTIVE_CLIENTS) ? data.data.ACTIVE_CLIENTS : []);
          }
        } catch (err) {
          setClients([]);
        } finally {
          setLoading(false);
          setIsApiLoading(false);
        }
      });
    };
    fetchClients();
  }, []);
  

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
      // fetch data for newPage here
    }
  };

  const handleJumpPage = () => {
    const num = Number(jumpPage);
    if (num >= 1 && num <= totalPages) {
      setPage(num);
      // fetch data for num here
    }
  };


  // Fetch summary data
  const fetchSummary = async (params = {}) => {
    if (!selectedClientId) return;
    setLoading(true);
    await callApi(async () => {
      setIsApiLoading(true);
      try {
        const payload = {
          clientId: selectedClientId,
          pageNo: params.page || page,
          pageSize: params.pageSize || pageSize,
          searchTerm: params.searchTerm !== undefined ? params.searchTerm : searchTerm,
          sortField: params.sortField || sortField,
          sortOrder: params.sortOrder || sortOrder,
        };
        const response = await fetch(`${config.API_BASE_URL}/clientdata/loadDataUploadSummary`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Username': username, 'Authorization': `Bearer ${mrToken}` },
          body: JSON.stringify(payload),
        });

		if (response.ok) {
		  const res = await response.json();
		  setData(res.data.SEARCH_LIST || []);
		  setTotalRecords(res.data.total || 0);
		  setTotalPages(Math.max(1, Math.ceil((res.data.total || 0) / (params.pageSize || pageSize))));
		} else {
		  setData([]);
		  setTotalRecords(0);
		  setTotalPages(1);
		}

      } catch (err) {
        setData([]);
        setTotal(0);
      } finally {
        setLoading(false);
        setIsApiLoading(false);
      }
    });
  };
  

  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setPage(1);
  };


  // When client changes, reset and fetch
  const handleClientChange = (e) => {
    setSelectedClientId(e.target.value);
    setPage(1);
    setSearchTerm('');
    setSortField('file_date');
    setSortOrder('desc');
  };

  // Fetch when client, page, pageSize, sort, or search changes
  useEffect(() => {
    if (selectedClientId) fetchSummary({ page: 1 });
    // eslint-disable-next-line
  }, [selectedClientId]);

  useEffect(() => {
    if (selectedClientId) fetchSummary();
    // eslint-disable-next-line
  }, [page, pageSize, sortField, sortOrder]);

  // Search
  const handleSearch = () => {
    setPage(1);
    fetchSummary({ page: 1, searchTerm });
  };

  // Clear search
  const handleClear = () => {
    setSearchTerm('');
    setPage(1);
    fetchSummary({ page: 1, searchTerm: '' });
    if (searchInputRef.current) searchInputRef.current.value = '';
  };

  // Handle enter key for search and jump
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  // Sort
  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };



  // Jump to page
 
 

  return (
    <>
      <Header />
      <div className="container-fluid text-secondary">
        <br /><br /><br /><br />
        <div className="card">
          <div className="card-header">View Client Data Upload Summary</div>
          <div className="card-body custom-card-bg">
            <div className="card">
              <div className='card-body'>
                <div className="tab-content p-3 border-0">
                  <div className='row'>
                    <div className="form-group col-md-3">
                      <label htmlFor="client_select" className="text-info fw-bold">Select Client</label>
                      <select
                        id="client_select"
                        className="form-select"
                        value={selectedClientId}
                        onChange={handleClientChange}
                      >
                        <option value="">Select Client</option>
                        {clients.map((client, idx) => (
                          <option key={idx} value={client.clientId || client.id}>
                            {client.clientName || client.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
                <div id="data_summary_loader" className="mt-3">
                  {loading ? (
                    <div>Loading...</div>
                  ) : (
                    <>
                      <div className="d-flex mb-2">
                        <input
                          type="text"
                          className="form-control me-2"
                          placeholder="Search by file name or text"
                          value={searchTerm}
                          ref={searchInputRef}
                          onChange={e => setSearchTerm(e.target.value)}
                          onKeyDown={handleKeyDown}
                          style={{ maxWidth: 300 }}
                        />
                        <button className="btn btn-outline-info me-2" onClick={handleSearch}>
                          <i className="fa fa-search"></i>
                        </button>
                        <button className="btn btn-outline-info me-2" onClick={handleClear}>
                          <i className="fa fa-trash"></i>
                        </button>
                        <select
                          className="form-select"
                          style={{ width: 100 }}
                          value={pageSize}
                          onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
                        >
                          {PAGE_SIZE_OPTIONS.map(size => (
                            <option key={size} value={size}>{size} / page</option>
                          ))}
                        </select>
                      </div>
                      <div className="table-responsive">
					  <table className="table table-bordered table-striped table-hover">
					    <thead>
					      <tr>
					        <th>No.</th>
					        <th onClick={() => handleSort('client_id')} style={{ cursor: 'pointer' }}>
					          Client ID {sortField === 'client_id' && (sortOrder === 'asc' ? '▲' : '▼')}
					        </th>
					        <th>Client</th>
					        <th onClick={() => handleSort('filename')} style={{ cursor: 'pointer' }}>
					          Filename {sortField === 'filename' && (sortOrder === 'asc' ? '▲' : '▼')}
					        </th>
					        <th onClick={() => handleSort('file_date')} style={{ cursor: 'pointer' }}>
					          File Date {sortField === 'file_date' && (sortOrder === 'asc' ? '▲' : '▼')}
					        </th>
					        <th>Uploaded By</th>
					        <th>No. of Rows</th>
					        {/* Add more columns as needed */}
					      </tr>
					    </thead>
					    <tbody>
					      {data.length === 0 ? (
					        <tr>
					          <td colSpan={7} className="text-center">No data found</td>
					        </tr>
					      ) : (
					        data.map((row, idx) => (
					          <tr key={row._id || idx}>
					            <td>{(page - 1) * pageSize + idx + 1}</td>
					            <td>{row.client_id}</td>
					            <td>{row.client}</td>
					            <td>{row.filename}</td>
					            <td>{row.file_date ? new Date(row.file_date).toLocaleString() : ''}</td>
					            <td>{row.uploaded_by}</td>
					            <td>{row.no_of_rows}</td>
					            {/* Add more cells as needed */}
					          </tr>
					        ))
					      )}
					    </tbody>
					  </table>
                      </div>
                      {/* Pagination */}

					  <nav>
					    <ul className="pagination mb-0">
					      <li className={`page-item${page === 1 ? ' disabled' : ''}`}>
					        <button className="page-link text-secondary" onClick={() => handlePageChange(1)} disabled={page === 1}>&laquo;</button>
					      </li>
					      <li className={`page-item${page === 1 ? ' disabled' : ''}`}>
					        <button className="page-link text-secondary" onClick={() => handlePageChange(page - 1)} disabled={page === 1}>Previous</button>
					      </li>
					      {pageNumbers.map(p => (
					        <li key={p} className={`page-item${page === p ? ' active' : ''}`}>
					          <a
					            href="#"
					            className="page-link"
					            onClick={e => { e.preventDefault(); handlePageChange(p); }}
					          >
					            {p}
					          </a>
					        </li>
					      ))}
					      <li className={`page-item${page === totalPages ? ' disabled' : ''}`}>
					        <button className="page-link text-secondary" onClick={() => handlePageChange(page + 1)} disabled={page === totalPages}>Next</button>
					      </li>
					      <li className={`page-item${page === totalPages ? ' disabled' : ''}`}>
					        <button className="page-link text-secondary" onClick={() => handlePageChange(totalPages)} disabled={page === totalPages}>&raquo;</button>
					      </li>
					      <li>
					        <div className="d-flex align-items-center ms-2">
					          <input
					            type="number"
					            min="1"
					            max={totalPages}
					            value={jumpPage}
					            onChange={e => setJumpPage(e.target.value)}
					            onKeyDown={e => { if (e.key === 'Enter') handleJumpPage(); }}
					            className="form-control"
					            style={{ width: 80, marginRight: 4 }}
					            placeholder="Page #"
					            aria-label="Go to page"
					          />
					          <button className="btn btn-secondary btn-sm" onClick={handleJumpPage} type="button">Go</button>
					        </div>
					      </li>
					      <li>
					        <span className="ms-3">Page {page} of {totalPages} - {totalRecords} Records</span>
					      </li>
					    </ul>
					  </nav>

                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default DisplayClientDataInfo;
